import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useSendPushNotificationMutation,
  useGetNotificationHistoryQuery,
} from "@/store/usersApi";

export const AdminNotificationsPage = () => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState<"all" | "specific">("all");
  const [userIdsRaw, setUserIdsRaw] = useState("");
  const [lastResult, setLastResult] = useState<{ sent: number; failed: number } | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const [sendPush, { isLoading: isSending }] = useSendPushNotificationMutation();
  const { data: history, isLoading: isHistoryLoading, refetch } = useGetNotificationHistoryQuery();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendError(null);
    setLastResult(null);

    const payload =
      target === "all"
        ? { title, body, allUsers: true as const }
        : {
            title,
            body,
            userIds: userIdsRaw
              .split(/[\s,]+/)
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !isNaN(n)),
          };

    try {
      const result = await sendPush(payload).unwrap();
      setLastResult(result);
      setTitle("");
      setBody("");
      setUserIdsRaw("");
      refetch();
    } catch (err: any) {
      setSendError(err?.data?.detail || "Ошибка при отправке уведомлений");
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-6">
        <Link
          to="/admin/"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
        >
          ← Назад
        </Link>
      </div>

      <h1 className="text-2xl font-semibold mb-6">Push-уведомления</h1>

      {/* Send form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl border border-gray-200 p-6 mb-8 space-y-4"
      >
        <h2 className="text-lg font-medium">Отправить уведомление</h2>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Заголовок
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={100}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Заголовок уведомления"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Текст сообщения
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            rows={4}
            maxLength={500}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Текст push-уведомления"
          />
        </div>

        <div>
          <p className="block text-sm font-medium text-gray-700 mb-2">
            Получатели
          </p>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                value="all"
                checked={target === "all"}
                onChange={() => setTarget("all")}
                className="accent-blue-600"
              />
              <span className="text-sm">Всем пользователям</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                value="specific"
                checked={target === "specific"}
                onChange={() => setTarget("specific")}
                className="accent-blue-600"
              />
              <span className="text-sm">Конкретным пользователям</span>
            </label>
          </div>
        </div>

        {target === "specific" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID пользователей
            </label>
            <textarea
              value={userIdsRaw}
              onChange={(e) => setUserIdsRaw(e.target.value)}
              required
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Через запятую или пробел: 123, 456, 789"
            />
          </div>
        )}

        <button
          type="submit"
          disabled={isSending}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
        >
          {isSending ? "Отправка..." : "🔔 Отправить"}
        </button>

        {lastResult && (
          <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-800">
            Отправлено: <strong>{lastResult.sent}</strong>, Ошибок:{" "}
            <strong>{lastResult.failed}</strong>
          </div>
        )}

        {sendError && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
            {sendError}
          </div>
        )}
      </form>

      {/* History */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-medium mb-4">История рассылок</h2>
        {isHistoryLoading ? (
          <p className="text-sm text-gray-500">Загрузка...</p>
        ) : !history || history.length === 0 ? (
          <p className="text-sm text-gray-500">История рассылок пуста.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 font-medium">
                  <th className="pb-3 pr-4">Дата</th>
                  <th className="pb-3 pr-4">Заголовок</th>
                  <th className="pb-3 pr-4">Сообщение</th>
                  <th className="pb-3 pr-4 text-right">Отправлено</th>
                  <th className="pb-3 text-right">Ошибок</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr
                    key={item.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 pr-4 text-gray-500 whitespace-nowrap">
                      {new Date(item.createdAt).toLocaleString("ru-RU", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="py-3 pr-4 font-medium text-gray-800">
                      {item.title}
                    </td>
                    <td className="py-3 pr-4 text-gray-600 max-w-xs truncate">
                      {item.body}
                    </td>
                    <td className="py-3 pr-4 text-right text-green-700 font-medium">
                      {item.sentCount}
                    </td>
                    <td className="py-3 text-right text-red-600 font-medium">
                      {item.failedCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
